import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Collapse, SxProps } from '@mui/material';
import { Theme, styled } from '@mui/material/styles';
import { getFeedbackUser } from '@/hooks/apiHooks';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import AngleBracketIcon from '@/public/icons/angle_bracket.svg';
import ProfileCardPopper from '@/components/@IntranetLibrary/ProfileCard/ProfileCardPopper';

// Inline heart icon from design HTML - uses fill/stroke for liked/unliked states
const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12.4209 6.49316C14.1392 4.77497 16.9253 4.77489 18.6436 6.49316C20.3617 8.21144 20.3617 10.9975 18.6436 12.7158L12.2793 19.0801C12.1232 19.236 11.87 19.2359 11.7139 19.0801L5.34961 12.7158C3.6313 10.9975 3.6313 8.21147 5.34961 6.49316C7.06792 4.77486 9.85396 4.77486 11.5723 6.49316C11.8066 6.72748 12.1866 6.72748 12.4209 6.49316Z"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SummaryHeartIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M15.8883 10.9499L10.7029 16.1353C10.3123 16.5258 9.67918 16.5258 9.28865 16.1353L4.1032 10.9499C2.47602 9.32267 2.47602 6.68449 4.1032 5.0573C5.73039 3.43012 8.36857 3.43012 9.99576 5.0573C11.6229 3.43012 14.2611 3.43012 15.8883 5.0573C17.5155 6.68449 17.5155 9.32267 15.8883 10.9499Z"
      fill={isOpen ? '#FFFFFF' : '#0066CC'}
    />
  </svg>
);
import usePopper from '@/hooks/usePopper';

type DesktopCommentLikeSectionProps = {
  documentId: string;
  isLike: boolean;
  isLikePending?: boolean;
  disabled?: boolean;
  onLikeClick?: () => void;
  sx?: SxProps<Theme>;
};

const LIKE_SECTION_CONTROL_HEIGHT = 48;

// Container: row layout with summary on left and like button on right
const Container = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
});

// Left summary area: clickable to expand/collapse (no hover when empty)
const SummaryButton = styled('button')<{ isOpen: boolean; hasLikes: boolean }>(
  ({ isOpen, hasLikes }) => ({
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    cursor: hasLikes ? 'pointer' : 'default',
    backgroundColor: hasLikes && isOpen ? 'rgba(0, 102, 204, 0.08)' : '#F3F5F6',
    transition: 'background-color 0.2s',
    textAlign: 'left',
    minWidth: 0,
    minHeight: LIKE_SECTION_CONTROL_HEIGHT,
    boxSizing: 'border-box',
    // Only show hover effect when there are likes
    '&:hover': hasLikes
      ? {
          backgroundColor: isOpen ? 'rgba(0, 102, 204, 0.12)' : '#E9EBEE',
        }
      : {},
    '&:disabled': {
      cursor: 'default',
    },
  }),
);

// Avatar stack with overlapping images
const AvatarStack = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  '& .avatar-image': {
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: '2px solid var(--dk-bg, #FFFFFF)',
    objectFit: 'cover',
    backgroundColor: 'var(--dk-bg, #FFFFFF)',
  },
  '& .avatar-image + .avatar-image': {
    marginLeft: -10,
  },
});

// Heart icon in collapsed state (sits after avatars)
const HeartIconWrapper = styled('span')<{
  isOpen: boolean;
  hasAvatars: boolean;
}>(({ isOpen, hasAvatars }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  marginLeft: !isOpen && hasAvatars ? -18 : 0,
  borderRadius: '50%',
  backgroundColor: isOpen ? '#0066CC' : '#FFFFFF',
  border: 'none',
  flexShrink: 0,
  boxSizing: 'border-box',
  '& svg': {
    width: 20,
    height: 20,
  },
}));

// Summary text
const SummaryText = styled('span')({
  flex: 1,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '22px',
  color: 'var(--dk-text-primary, #3F4254)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '& strong': {
    color: 'var(--dk-accent, #0066CC)',
    fontWeight: 700,
  },
});

// Chevron icon with rotation
const ChevronWrapper = styled('span')<{ isOpen: boolean; hasLikes: boolean }>(
  ({ isOpen, hasLikes }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: hasLikes ? '#555962' : '#D1D8E2',
    transition: 'transform 0.2s',
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    '& svg': {
      width: 24,
      height: 24,
    },
    '& svg path': {
      stroke: 'currentColor',
    },
  }),
);

// Right like button (square with rounded corners) - secondary button style with design tokens
const LikeButton = styled('button')<{ isLiked: boolean }>(({ isLiked }) => ({
  width: 48,
  height: LIKE_SECTION_CONTROL_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  border: '1px solid var(--dk-line, #E7E9EC)',
  backgroundColor: 'var(--dk-bg, #FFFFFF)',
  color: isLiked ? '#0066CC' : '#555962',
  cursor: 'pointer',
  flexShrink: 0,
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
    borderColor: 'var(--dk-line-strong, #D7DBE0)',
  },
  '&:active': {
    backgroundColor: 'var(--dk-bg-muted, #F3F5F6)',
    borderColor: 'var(--dk-line-strong, #C9CED4)',
  },
  '&:focus-visible': {
    outline: 'none',
    borderColor: 'var(--dk-line-strong, #D7DBE0)',
    boxShadow: '0 0 0 2px rgba(85, 89, 98, 0.12)',
  },
  '&:disabled': {
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'var(--dk-bg, #FFFFFF)',
      borderColor: 'var(--dk-line, #E7E9EC)',
    },
  },
  '& svg': {
    width: 24,
    height: 24,
  },
}));

// Expanded user list container (matches bs-likes-chips)
const UserListContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  padding: '8px',
  marginTop: '12px',
  borderRadius: '8px',
  backgroundColor: 'var(--dk-bg-muted, #F3F5F6)',
});

// User chip (pill with avatar + name) - matches bs-chip
const UserChip = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 12px 4px 4px',
  border: '1px solid var(--dk-line, #E7E9EC)',
  backgroundColor: 'var(--dk-bg, #FFFFFF)',
  borderRadius: '999px',
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
  },
  '& .chip-avatar': {
    width: 32,
    height: 32,
    borderRadius: '50%',
    objectFit: 'cover',
  },
  '& .chip-name': {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: 'var(--dk-text-primary, #34383D)',
    whiteSpace: 'nowrap',
  },
  // Small like badge on avatar - matches bs-chip-badge (12px)
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 26,
    bottom: 4,
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: '1px solid var(--dk-bg, #FFFFFF)',
    backgroundImage: "url('/icons/like_circle_on.svg')",
    backgroundSize: 'cover',
  },
});

// Fold/hide button - matches bs-likes-hide (16px font)
const FoldButton = styled('button')({
  display: 'inline-flex',
  padding: 0,
  marginTop: '12px',
  border: 'none',
  background: 'none',
  color: 'var(--dk-accent, #0066CC)',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const DesktopCommentLikeSection: React.FC<DesktopCommentLikeSectionProps> = ({
  documentId,
  isLike,
  isLikePending,
  disabled,
  onLikeClick,
  sx,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading } = getFeedbackUser(documentId);

  const likeCount = Array.isArray(data) ? data.length : 0;
  const avatars = Array.isArray(data) ? data.slice(0, 4) : [];
  const hasLikes = likeCount > 0;
  const hasAvatars = avatars.length > 0;

  useEffect(() => {
    if (!hasLikes && isOpen) {
      setIsOpen(false);
    }
  }, [hasLikes, isOpen]);

  // Profile card popper for user chips
  const [passportId, setPassportId] = useState<string>('');
  const { anchorEl, openPopper, closePopper } = usePopper<HTMLButtonElement>(
    (el) => {
      const userId = el.dataset?.['userId'];
      setPassportId(userId ?? '');
    },
  );

  const renderSummaryText = () => {
    if (isLoading) {
      return <SummaryText>불러오는 중...</SummaryText>;
    }
    if (likeCount === 0) {
      return (
        <SummaryText>
          게시물에 첫번째로 <strong>좋아요</strong>를 눌러보세요.
        </SummaryText>
      );
    }
    return (
      <SummaryText>
        <strong>{likeCount}</strong>명의 프프인이 이 게시물을 좋아합니다.
      </SummaryText>
    );
  };

  const handleSummaryClick = () => {
    if (hasLikes) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <Box sx={sx}>
      <Container>
        <SummaryButton
          type="button"
          isOpen={isOpen}
          hasLikes={hasLikes}
          disabled={disabled}
          onClick={handleSummaryClick}
          aria-expanded={isOpen}
        >
          {/* Show avatars only when collapsed */}
          {!isOpen && hasAvatars && (
            <AvatarStack>
              {avatars.map((item) => (
                <ImageWithErrorHandling
                  key={item.passportId}
                  src={imgUrlChk(item.profileImageUrl)}
                  width={32}
                  height={32}
                  alt={`${item.name}`}
                  className="avatar-image"
                />
              ))}
            </AvatarStack>
          )}
          <HeartIconWrapper isOpen={isOpen} hasAvatars={hasAvatars}>
            <SummaryHeartIcon isOpen={isOpen} />
          </HeartIconWrapper>
          {renderSummaryText()}
          <ChevronWrapper isOpen={isOpen} hasLikes={hasLikes}>
            <AngleBracketIcon />
          </ChevronWrapper>
        </SummaryButton>

        <LikeButton
          type="button"
          isLiked={isLike}
          disabled={disabled || isLikePending}
          onClick={onLikeClick}
          aria-label={isLike ? '좋아요 취소' : '좋아요'}
          aria-busy={isLikePending}
        >
          {isLikePending ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <HeartIcon filled={isLike} />
          )}
        </LikeButton>
      </Container>

      {/* Expanded user list */}
      {hasLikes && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <UserListContainer>
            {Array.isArray(data) &&
              data.map((item) => (
                <UserChip
                  key={item.passportId}
                  type="button"
                  data-user-id={item.passportId}
                  onClick={openPopper}
                >
                  <ImageWithErrorHandling
                    src={imgUrlChk(item.profileImageUrl)}
                    width={32}
                    height={32}
                    alt={`${item.name}`}
                    className="chip-avatar"
                  />
                  <span className="chip-name">
                    {[item.name, item.positionName].filter(Boolean).join(' ')}
                  </span>
                </UserChip>
              ))}
          </UserListContainer>
          <FoldButton type="button" onClick={() => setIsOpen(false)}>
            좋아요 내역 숨기기
          </FoldButton>
        </Collapse>
      )}

      <ProfileCardPopper
        passportId={passportId}
        anchorEl={anchorEl}
        onClose={closePopper}
      />
    </Box>
  );
};

export default DesktopCommentLikeSection;
